import { useForm } from 'react-form-state-manager';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';

import { Locale } from '__generated__/graphql';

import { EditorProps } from '../../common/Tiptap/types';
import { Translation } from './helpers';
import Editor from '../../common/Tiptap';
import UI from '../../common/UI';
import useLocale from '../../common/useLocale';

export interface EditTranslationsModalProps {
  defaultLocale: Locale;
  defaultValue: string | null;
  translations: Translation[];
  title?: string;
  inputType?: InputType;
  maxLength?: number;
  onSave?: (defaultValue: string | null, translations: Translation[]) => void;
  editorProps?: Partial<EditorProps>;
}

type InputType = 'string' | 'text';

export const EditTranslationsModal = ({
  defaultLocale, defaultValue, translations, title, inputType = 'string', maxLength, onSave, editorProps,
}: EditTranslationsModalProps) => {
  const { t } = useTranslation();
  const { sortedLocales } = useLocale();

  // 1) Remove the translation for the default locale if its present in the list of translations.
  // 2) Add the given default locale and value to the list of translations.
  // 3) Add the translations to the form, keyed by locale.
  const form = useForm({
    values: [
      ...translations.filter((translation) => translation.locale !== defaultLocale),
      {
        locale: defaultLocale,
        value: defaultValue,
      },
    ].reduce((translations, translation) => ({
      ...translations,
      [translation.locale]: translation.value,
    }), {}) as { [key in Locale]: string | null },
  });

  const save = () => {
    onSave(form.values[defaultLocale], Object.keys(form.values).map((locale: Locale) => ({
      locale,
      value: form.values[locale] || null, // Unset the translation when empty.
    })));
  };

  // Default locale first, followed by the other locales sorted by language name.
  const locales = [
    defaultLocale,
    ...sortedLocales.filter((locale) => locale !== defaultLocale),
  ];

  return (
    <UI.Form onSubmit={save}>
      <UI.FormGrid>
        <UI.H4>
          {title || t('translations')}
        </UI.H4>

        <UI.HR />

        {locales.map((locale) => (
          <UI.InputGroup
            sc={{ mb: 0 }}
            key={locale}
          >
            <UI.InputLabel htmlFor={`Translation_${locale}`} id={`TranslationLabel_${locale}`}>
              {locale === defaultLocale && (
                <>
                  {t('default')}
                  {` – ${t(`common:locales.${locale}`)}`}
                </>
              )}
              {locale !== defaultLocale && t(`common:locales.${locale}`)}
            </UI.InputLabel>
            {locale === defaultLocale && (
              <UI.InputDescription>
                {t('default_language_description')}
              </UI.InputDescription>
            )}
            {inputType === 'string' && (
              <UI.Input
                id={`Translation_${locale}`}
                maxLength={maxLength}
                {...form.text(locale)}
                placeholder={form.get(defaultLocale)}
              />
            )}
            {inputType === 'text' && (
              <Editor
                value={form.values[locale]}
                onChange={(value) => form.set(locale, value)}
                stickyMenuBar={false}
                {...editorProps}
                attributes={{
                  ...editorProps?.attributes,
                  id: `Translation_${locale}`,
                  'aria-labelledby': `TranslationLabel_${locale}`,
                }}
              />
            )}
          </UI.InputGroup>
        ))}

        <UI.HR />

        <UI.Div sc={{ textAlign: 'right' }}>
          <UI.Button
            type="submit"
            sc={{ brand: 'secondary' }}
          >
            {t('continue')}
          </UI.Button>
        </UI.Div>
      </UI.FormGrid>
    </UI.Form>
  );
};

interface UseEditTranslationsModalProps {
  defaultLocale: Locale,
  defaultValue: string | null,
  translations: Translation[],
  title?: string,
  inputType?: InputType;
  maxLength?: number;
  onSave: (defaultValue: string | null, translations: Translation[]) => void;
  editorProps?: Partial<EditorProps>;
}

const useEditTranslationsModal = ({
  defaultLocale, defaultValue, translations, title, inputType, maxLength, onSave, editorProps,
}: UseEditTranslationsModalProps) => {
  const [openModal, closeModal] = useModal(() => {
    const save = (defaultValue: string | null, translations: Translation[]) => {
      onSave(defaultValue, translations);
      closeModal();
    };

    return (
      <UI.Modal maxWidth={inputType === 'text' ? 800 : 600} close={closeModal}>
        <EditTranslationsModal
          defaultLocale={defaultLocale}
          defaultValue={defaultValue}
          translations={translations}
          title={title}
          inputType={inputType}
          maxLength={maxLength}
          onSave={save}
          editorProps={editorProps}
        />
      </UI.Modal>
    );
  }, [defaultLocale, defaultValue, translations, title, inputType, maxLength, editorProps, onSave]);

  return [openModal, closeModal];
};

export default useEditTranslationsModal;
